/**
 * StagedSendTile — the staged-email card as a live, editable draft box
 * (2026-09-17).
 *
 * Owner: "every aspect of the card the user should have the ability to edit and
 * submit real quick as a draft box stays a draft updated for all users in
 * thread instantly as user makes a change and extremely user friendly."
 *
 * WHY REACT AND NOT THE INJECTED HTML. The html lane rebuilds the whole subtree
 * on every render (the anti-race rule that keeps it correct), which destroys
 * focus, caret and uncommitted keystrokes the moment a remote update lands —
 * the failure already recorded in connectTileRehydrate for a mere button
 * repaint. Reconciliation is what replaces that rule here.
 *
 * IT DEGRADES TO THE ORDINARY TILE, ALWAYS. Until the listener has rows — and
 * permanently if it is refused — this renders `fallback`: the very
 * SpecialMessageTile this message would have rendered without the lever, built
 * by the transcript and handed in. So an old bundle, another device, a denied
 * listener and a switched-off lever all keep the card they always had, with
 * its text, ⓘ popup, copy and read-aloud intact — not a button row alone.
 *
 * The visuals are the card kit's own stylesheet, rendered into this tree, so
 * the React tile and the html tile cannot drift apart visually.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ATTACHMENT_ISSUE_WORDS,
  P0AC,
  actionCardStyles,
  attachmentSourceWords,
  attachmentsBlockSend,
  humanBytes,
  splitRecipients,
} from '../../services/globalTools/tools/actionCardKit';
import type { StagedSendRow } from '../../services/globalTools/tools/authorizeCardHtml';
import { useStagedSendBatch } from '../../services/globalTools/tools/useStagedSendBatch';
import {
  decidePendingAction,
  editPendingAction,
  type StagedSendEditPatch,
} from '../../services/prest0nSession/client';
import { actionDecidedDetail, emitActionDecided } from '../../services/globalTools/tools/actionDecidedEvent';

/** How long a field rests before its change is sent. Short enough that the
 *  other people in the thread see it as typing, long enough that a sentence
 *  is one write rather than forty. */
const EDIT_DEBOUNCE_MS = 500;

export interface StagedSendTileProps {
  batchId: string;
  threadId?: string | null;
  /** What the tile falls back to whenever the live lane cannot serve: the
   *  ORDINARY special tile this message would have rendered, passed in as a
   *  node rather than rebuilt here. Passing html instead would have kept the
   *  buttons and silently dropped the message's text, its ⓘ usage popup, copy
   *  and read-aloud — and would have needed a raw-html sink to render. */
  fallback?: React.ReactNode;
  /** False once a newer message supersedes the card's workflow step. The send
   *  controls are a STANDING decision and stay live regardless (the same
   *  exemption the html lane's button ids carry); this only dims editing. */
  ctaLive?: boolean;
}

type FieldKey = 'to' | 'cc' | 'bcc' | 'subject' | 'body';

interface RowEditState {
  /** Values the person is typing, which must never be clobbered by a remote
   *  update while they are in the field. */
  local: Partial<Record<FieldKey, string>>;
  focused: FieldKey | null;
  saving: boolean;
  refused: string | null;
  /**
   * What is typed in the "attach a file" box. Deliberately NOT a FieldKey and
   * NOT debounced: a filename is committed on Enter, because a half-typed name
   * saved after 500ms would resolve to nothing and put "cont — not found in
   * this conversation" on everyone's card mid-keystroke.
   */
  fileDraft: string;
}

const EMPTY_EDIT: RowEditState = {
  local: {}, focused: null, saving: false, refused: null, fileDraft: '',
};

/**
 * The file names on a row, in the order the card shows them — the attached one
 * first, then everything that is not attached. This is the list the edit sends,
 * so the order is a decision: the FIRST name is the one that attaches, and
 * showing the attached file first means a removal elsewhere cannot silently
 * swap which document rides as the attachment. A newly added name goes on the
 * END for the same reason.
 */
function fileNamesOf(row: StagedSendRow): string[] {
  return [
    ...(row.attachments || []).map((a) => a.name),
    ...(row.attachmentIssues || []).map((i) => i.name),
  ].filter(Boolean);
}

function relativeTime(atMs: number, now: number): string {
  const secs = Math.max(0, Math.round((now - atMs) / 1000));
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

export const StagedSendTile: React.FC<StagedSendTileProps> = ({
  batchId,
  fallback,
  ctaLive = true,
}) => {
  const { rows, status } = useStagedSendBatch(batchId);
  const [edits, setEdits] = useState<Record<string, RowEditState>>({});
  const [deciding, setDeciding] = useState<Record<string, boolean>>({});
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  // The rev each row was last saved at, so a stale write loses rather than
  // clobbering a colleague's edit.
  const revs = useRef<Record<string, number | null>>({});

  useEffect(() => () => {
    for (const t of Object.values(timers.current)) clearTimeout(t);
  }, []);

  const editOf = useCallback((id: string): RowEditState => edits[id] || EMPTY_EDIT, [edits]);

  const patchEdit = useCallback((id: string, next: Partial<RowEditState>) => {
    setEdits((prev) => ({ ...prev, [id]: { ...(prev[id] || EMPTY_EDIT), ...next } }));
  }, []);

  const flush = useCallback(async (row: StagedSendRow, patch: StagedSendEditPatch) => {
    patchEdit(row.actionId, { saving: true, refused: null });
    const result = await editPendingAction(row.actionId, patch, revs.current[row.actionId] ?? null);
    if (result.ok) {
      revs.current[row.actionId] = result.rev;
      // The listener delivers the saved values back; drop the local copy so
      // the document is the truth again.
      patchEdit(row.actionId, { saving: false, local: {}, refused: null });
      return;
    }
    if (result.kind === 'conflict') {
      // Somebody else wrote the same field first. Take theirs — their version
      // is already on screen via the listener — and say nothing louder than
      // that, because a draft box is not a form.
      revs.current[row.actionId] = result.rev;
      patchEdit(row.actionId, { saving: false, local: {} });
      return;
    }
    patchEdit(row.actionId, {
      saving: false,
      // The server's own wording (R7 for a refusal); never a sentence composed
      // here.
      refused: result.kind === 'forbidden'
        ? result.message
        : result.kind === 'gone'
          ? 'this draft is no longer waiting — it has already been decided.'
          : 'that change did not save. try again.',
    });
  }, [patchEdit]);

  const onFieldChange = useCallback((row: StagedSendRow, field: FieldKey, value: string) => {
    patchEdit(row.actionId, {
      local: { ...editOf(row.actionId).local, [field]: value },
      focused: field,
    });
    const key = `${row.actionId}:${field}`;
    if (timers.current[key]) clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(() => {
      void flush(row, { [field]: value } as StagedSendEditPatch);
    }, EDIT_DEBOUNCE_MS);
  }, [editOf, flush, patchEdit]);

  /**
   * Add or remove a file by handing back the WHOLE desired list of names. The
   * server re-resolves every one of them against whoever is editing, which is
   * the authorization — so the card never sends a path, and a name it does not
   * recognise comes back named on the card rather than silently dropped.
   */
  const onFilesChange = useCallback((row: StagedSendRow, names: string[]) => {
    void flush(row, { attachments: names });
  }, [flush]);

  const onAddFile = useCallback((row: StagedSendRow) => {
    const typed = editOf(row.actionId).fileDraft.trim();
    if (!typed) return;
    patchEdit(row.actionId, { fileDraft: '' });
    // Appended, never inserted: adding a file must not change which document
    // rides as the attachment.
    onFilesChange(row, [...fileNamesOf(row), typed]);
  }, [editOf, onFilesChange, patchEdit]);

  const onDecide = useCallback(async (row: StagedSendRow, decision: 'authorize' | 'cancel') => {
    if (deciding[row.actionId]) return;
    setDeciding((p) => ({ ...p, [row.actionId]: true }));
    try {
      const outcome = await decidePendingAction(row.actionId, decision);
      // The ROW itself updates from the document — the listener carries the
      // new status and confirmation — so nothing is stamped here. The event is
      // what the rest of the chat listens for, and both lanes emit it the same
      // way through one helper.
      emitActionDecided(
        actionDecidedDetail(row.actionId, outcome, { to: row.to, subject: row.subject }),
      );
    } finally {
      setDeciding((p) => ({ ...p, [row.actionId]: false }));
    }
  }, [deciding]);

  const now = Date.now();
  const solo = rows?.length === 1;

  const counts = useMemo(() => {
    const all = rows || [];
    const sent = all.filter((r) => r.state === 'sent' || r.state === 'auto-sent').length;
    const awaiting = all.filter((r) => r.state === 'pending' || r.state === 'sending').length;
    const skipped = all.filter((r) => r.state === 'skipped').length;
    return { sent, awaiting, skipped, other: all.length - sent - awaiting - skipped };
  }, [rows]);

  // THE FALLBACK. No rows yet, or a listener that cannot serve, renders exactly
  // what shipped before this component existed.
  if (!rows || rows.length === 0 || status === 'unavailable') {
    return <>{fallback ?? null}</>;
  }

  const renderRecipients = (row: StagedSendRow, field: 'to' | 'cc' | 'bcc', label: string) => {
    const state = editOf(row.actionId);
    const value = state.local[field] ?? (field === 'bcc' ? (row.bcc || '') : row[field] || '');
    const chips = splitRecipients(value);
    if (!chips.length && field !== 'to') return null;
    const removeAt = (index: number) => {
      const next = chips.filter((_, i) => i !== index).join(', ');
      onFieldChange(row, field, next);
      void flush(row, { [field]: next } as StagedSendEditPatch);
    };
    return (
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
        <span className="p0ac-meta-label">{label}</span>
        {chips.map((address, i) => (
          <span key={`${address}-${i}`} className="p0ac-recip">
            {address}
            {ctaLive && (
              <button
                type="button"
                aria-label={`remove ${address}`}
                onClick={() => removeAt(i)}
                style={{
                  marginLeft: 6, border: 'none', background: 'transparent',
                  cursor: 'pointer', color: P0AC.SUBTLE, fontSize: '0.8rem', padding: 0,
                }}
              >×</button>
            )}
          </span>
        ))}
        <input
          type="text"
          value={state.focused === field ? (state.local[field] ?? value) : value}
          onChange={(e) => onFieldChange(row, field, e.target.value)}
          onFocus={() => patchEdit(row.actionId, { focused: field })}
          onBlur={() => patchEdit(row.actionId, { focused: null })}
          placeholder={field === 'to' ? 'add someone…' : `add ${field}…`}
          aria-label={`${label} recipients`}
          style={{
            flex: '1 1 160px', minWidth: 120, border: 'none', outline: 'none',
            background: 'transparent', font: 'inherit', color: P0AC.INK, padding: '2px 0',
          }}
        />
      </div>
    );
  };

  const renderRow = (row: StagedSendRow) => {
    const state = editOf(row.actionId);
    const actionable = row.state === 'pending' || (row.state === 'failed' && row.retryable);
    // A named file that is not going out HOLDS the send — the server refuses it
    // too (that is the enforcement), and disabling the button here is so nobody
    // taps a send that cannot happen. A file carried as a LINK is going out and
    // never holds it; the distinction lives in one place, `issueStillDelivers`.
    const filesHoldSend = attachmentsBlockSend(row.attachmentIssues);
    const subject = state.local.subject ?? row.subject;
    const body = state.local.body ?? row.body;
    const lastEdited = row.lastEditedBy;
    return (
      <div className={solo ? 'p0ac-row p0ac-row--solo' : 'p0ac-row'} key={row.actionId} id={`p0ac-row-${row.actionId}`}>
        <div className="p0ac-row-meta" style={{ fontSize: '0.84rem' }}>
          {!solo && (
            <div className="p0ac-row-eyebrow">
              #{row.batchIndex || '?'}{row.from ? ` · from: ${row.from}` : ''}
            </div>
          )}
          {solo && row.from && <div><span className="p0ac-meta-label">from:</span> {row.from}</div>}
          {renderRecipients(row, 'to', 'to:')}
          {renderRecipients(row, 'cc', 'cc:')}
          {renderRecipients(row, 'bcc', 'bcc:')}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span className="p0ac-meta-label">subject:</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => onFieldChange(row, 'subject', e.target.value)}
              onFocus={() => patchEdit(row.actionId, { focused: 'subject' })}
              onBlur={() => patchEdit(row.actionId, { focused: null })}
              aria-label="subject"
              disabled={!actionable}
              style={{
                flex: 1, border: 'none', outline: 'none', background: 'transparent',
                font: 'inherit', color: P0AC.INK, padding: '2px 0',
              }}
            />
          </div>
          {(!!row.attachments?.length || ctaLive) && (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
              <span className="p0ac-meta-label">attached:</span>
              {(row.attachments || []).map((a, i) => (
                // Keyed by POSITION as well as name: two files called
                // contract.pdf are a real thing to attach, and a name-only key
                // makes React drop the second one.
                <span className="p0ac-recip" key={`${a.name}-${i}`}>
                  {a.name}
                  {a.size ? ` · ${humanBytes(a.size)}` : ''}
                  {attachmentSourceWords(a.source) ? ` · ${attachmentSourceWords(a.source)}` : ''}
                  {ctaLive && actionable && (
                    <button
                      type="button"
                      aria-label={`remove ${a.name}`}
                      onClick={() => onFilesChange(row, fileNamesOf(row).filter((n) => n !== a.name))}
                      style={{
                        marginLeft: 6, border: 'none', background: 'transparent',
                        cursor: 'pointer', color: P0AC.SUBTLE, fontSize: '0.8rem', padding: 0,
                      }}
                    >×</button>
                  )}
                </span>
              ))}
              {ctaLive && actionable && (
                <input
                  type="text"
                  value={state.fileDraft}
                  onChange={(e) => patchEdit(row.actionId, { fileDraft: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    onAddFile(row);
                  }}
                  onBlur={() => onAddFile(row)}
                  placeholder="attach a file from this chat…"
                  aria-label="attach a file by name"
                  style={{
                    flex: '1 1 180px', minWidth: 140, border: 'none', outline: 'none',
                    background: 'transparent', font: 'inherit', color: P0AC.INK, padding: '2px 0',
                  }}
                />
              )}
            </div>
          )}
          {!!row.attachmentIssues?.length && (
            // THE HONEST HALF. The html card has always rendered this; the tile
            // did not, and since the tile became the primary surface that made
            // "contract.pdf — too big to attach" invisible exactly where a
            // person decides whether to send. Same words as the html lane, from
            // the kit, never a second sentence written here — and each row is
            // removable, because a file that is holding the send has to be
            // takeable off the draft by the person looking at it.
            <div className="p0ac-row-note" style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
              <span>not attached:</span>
              {row.attachmentIssues
                .filter((issue) => issue && issue.name && ATTACHMENT_ISSUE_WORDS[issue.reason])
                .map((issue, i) => (
                  <span key={`${issue.name}-${i}`}>
                    {issue.name} — {ATTACHMENT_ISSUE_WORDS[issue.reason]}
                    {ctaLive && actionable && (
                      <button
                        type="button"
                        aria-label={`remove ${issue.name}`}
                        onClick={() => onFilesChange(row, fileNamesOf(row).filter((n) => n !== issue.name))}
                        style={{
                          marginLeft: 6, border: 'none', background: 'transparent',
                          cursor: 'pointer', color: P0AC.SUBTLE, fontSize: '0.8rem', padding: 0,
                        }}
                      >×</button>
                    )}
                  </span>
                ))}
            </div>
          )}
        </div>

        <textarea
          value={body}
          onChange={(e) => onFieldChange(row, 'body', e.target.value)}
          onFocus={() => patchEdit(row.actionId, { focused: 'body' })}
          onBlur={() => patchEdit(row.actionId, { focused: null })}
          aria-label="email body"
          disabled={!actionable}
          rows={Math.min(18, Math.max(4, body.split('\n').length + 1))}
          className="p0ac-body"
          style={{
            width: '100%', border: 'none', outline: 'none', resize: 'vertical',
            background: 'transparent', font: 'inherit', color: P0AC.INK,
          }}
        />

        {lastEdited?.name && (
          <div className="p0ac-eyebrow" style={{ marginTop: 6 }}>
            ✎ edited by {lastEdited.name} · {relativeTime(lastEdited.atMs, now)}
          </div>
        )}
        {state.saving && <div className="p0ac-eyebrow" style={{ marginTop: 4 }}>saving…</div>}
        {state.refused && <div className="p0ac-row-note" style={{ marginTop: 4 }}>{state.refused}</div>}

        {actionable ? (
          <div className={solo ? 'p0ac-actions' : 'p0ac-btnrow'} style={{ marginTop: 10 }}>
            <button
              type="button"
              className={`p0ac-btn p0ac-btn--${solo ? 'md' : 'sm'} p0ac-btn--primary`}
              disabled={deciding[row.actionId] || filesHoldSend}
              title={filesHoldSend ? 'take the file off the draft, or attach it again' : undefined}
              onClick={() => void onDecide(row, 'authorize')}
            >
              {deciding[row.actionId] ? 'sending…' : (row.state === 'failed' ? 'try again' : 'send it')}
            </button>
            <button
              type="button"
              className={`p0ac-btn p0ac-btn--${solo ? 'md' : 'sm'} p0ac-btn--ghost`}
              disabled={deciding[row.actionId]}
              onClick={() => void onDecide(row, 'cancel')}
            >
              {solo ? "don't send" : 'skip'}
            </button>
          </div>
        ) : (
          <div className="p0ac-state" style={{ marginTop: 10, color: P0AC.SUBTLE }}>
            {row.state === 'sent' || row.state === 'auto-sent'
              ? 'sent — confirmed by your email provider'
              : row.state === 'skipped'
                ? 'skipped — you chose not to send this one'
                : row.state}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p0ac-card" style={{ maxWidth: solo ? 560 : 640 }}>
      <style>{actionCardStyles()}</style>
      <div className="p0ac-title">
        <span>{solo ? 'ready to send — your call' : `ready to send — your call, ${rows.length} staged`}</span>
      </div>
      {!solo && (
        <div className="p0ac-chips">
          <span className="p0ac-chip p0ac-chip--neutral">{rows.length} staged</span>
          {counts.sent > 0 && <span className="p0ac-chip p0ac-chip--ok">{counts.sent} sent</span>}
          {counts.awaiting > 0 && <span className="p0ac-chip p0ac-chip--neutral">{counts.awaiting} awaiting your call</span>}
          {counts.skipped > 0 && <span className="p0ac-chip p0ac-chip--muted">{counts.skipped} skipped</span>}
        </div>
      )}
      <div className="p0ac-note">
        {solo
          ? 'nothing goes out until you tap send it — you can change anything here first.'
          : 'nothing goes out until you tap send it — per row, or all at once. you can change anything here first.'}
      </div>
      <div className={rows.length >= 4 ? 'p0ac-rows p0ac-rows--scroll' : 'p0ac-rows'}>
        {rows.map(renderRow)}
      </div>
    </div>
  );
};

export default StagedSendTile;
